/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 *
 * @author Evgeniy Naumov
 * @version 1.26.0
 * @since 1.25.1
 */

import { FC, SyntheticEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button, TabItem, Typography } from '@nexign/scc-ui-kit';
import { Form, Formik, FormikProps } from 'formik';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { LinkBlock } from '@/main/components/common';
import { IDatePickerAdditionalButton } from '@/main/components/common/DatePicker/types/datePickerTypes';
import ArrowTooltip from '@/main/components/common/Tooltip/Tooltip';
import { appHistoryPush } from '@/main/modules/app/appHistory';
import FormFieldDate from '@/main/modules/form/components/FormFieldDate';
import FormFieldInput from '@/main/modules/form/components/FormFieldInput';
import FormFieldTextarea from '@/main/modules/form/components/FormFieldTextarea';
import FormFileInput from '@/main/modules/form/components/FormFileInput';
import FormImageLinks from '@/main/modules/form/components/FormImageLinks';
import {
    makeSelectPageNewsTranslation,
    makeSelectTranslationCommon,
} from '@/main/modules/internationalization/intSelectors';
import { KitLoader } from '@/main/modules/kitLoader';
import { modalCloseAll } from '@/main/modules/modal/modalActions';
import { EModalStatus } from '@/main/modules/modal/types/modalTypes';
import { ROUTE_NEWS_ID, RoutePath } from '@/main/routes/constants/routesConstants';
import { FormSubmitHandler } from '@/main/types/globalTypes';

import {
    ECreateNewsTypes,
    ERecipientTypes,
    ENewsFormCreationFields,
    MAX_IMAGES,
    TODAY_FULL_DATE,
    defaultCreateInternalNewsFormValues,
    DEFAULT_RECIPIENTS_FILE_NAME,
} from '../../constants';
import {
    ButtonsTooltipWrapperStyled,
    ButtonsWrapperStyled,
    NewsFormControlsContainerStyled,
    NewsFormDateContainerStyled,
    NewsFormFileContainerStyled,
    NewsFormRepeatContainerStyled,
    RequiredHintAsteriskStyled,
    RequiredHintStyled,
    TabMenuStyled,
} from '../../styled/PageNewsCreateStyled';
import { ICreateInternalNewsForm, IPreviewLocationState } from '../../types';
import { base64toFile, fileToBase64 } from '../../utils/fileUtils';
import { getNewsInternalFormValidationSchema } from '../../validation';
import { useInternalNewsSubmit } from './hooks/useInternalNewsSubmit';
import { useOpenPublishModal } from './hooks/useOpenPublishModal';

interface INewsInternalFormProps {
    handleCancel: () => void;
    handleRedirect: () => void;
}

const NewsInternalForm: FC<INewsInternalFormProps> = ({ handleRedirect, handleCancel }) => {
    const dispatch = useDispatch();

    const { state: locationState } = useLocation<IPreviewLocationState>();

    const [fileTab, setFileTab] = useState<ERecipientTypes>(locationState?.recipientType || ERecipientTypes.INN);

    const handleTabChange = useCallback((ev: SyntheticEvent, value) => {
        ev.stopPropagation();
        setFileTab(value);
    }, []);

    const formikRef = useRef<FormikProps<ICreateInternalNewsForm>>(null);
    const {
        createNews: { requiredHint: tRequiredHint, form: tForm },
    } = useSelector(makeSelectPageNewsTranslation());
    const tCorporate = useSelector(makeSelectTranslationCommon());

    const history = useHistory();

    const [publish, { isLoading, isError, error }] = useInternalNewsSubmit();

    const openPublishModal = useOpenPublishModal();

    const validationSchema = getNewsInternalFormValidationSchema(tCorporate.form);

    const handleClearLocationState = useCallback(() => {
        history.replace({ state: null });
    }, [history]);

    const handleSubmit: FormSubmitHandler<ICreateInternalNewsForm> = (data, { setSubmitting, resetForm }) => {
        const handleCreate = () => {
            publish(data, fileTab, resetForm, setSubmitting);
        };
        openPublishModal(ECreateNewsTypes.INTERNAL, EModalStatus.start, { params: data }, handleCreate, setSubmitting);
    };

    const additionalPublishToButton: IDatePickerAdditionalButton[] = useMemo(() => {
        if (!formikRef.current) return [];
        const { setFieldValue } = formikRef.current;
        return [
            {
                onClick: () => setFieldValue(ENewsFormCreationFields.PUBLISH_TO, ''),
                title: tCorporate.calendar.additionalButtons.deselectDate,
            },
        ];

        // watch formikRef.current
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formikRef.current, tCorporate.calendar.additionalButtons.deselectDate]);

    useEffect(() => {
        if (isError) {
            dispatch(modalCloseAll());
            openPublishModal(ECreateNewsTypes.INTERNAL, EModalStatus.failure, { apiError: error });
        }
    }, [dispatch, error, isError, locationState, openPublishModal]);

    useEffect(() => {
        if (locationState && formikRef.current) {
            const { fileBase64, ...data } = locationState;
            const formValues: ICreateInternalNewsForm = {
                ...data,
            };
            // после возвращения со страницы превью декодируем файл и кладем его в стейт формы
            if (fileBase64) {
                const file = base64toFile(fileBase64, DEFAULT_RECIPIENTS_FILE_NAME);
                formValues[ENewsFormCreationFields.USER_FILE] = file;
            }
            formikRef.current.setValues(formValues);
            handleClearLocationState();
        }
    }, [handleClearLocationState, locationState]);

    return (
        <>
            <KitLoader showLoader={isLoading} variant="global" />
            <Formik<ICreateInternalNewsForm>
                enableReinitialize
                initialValues={defaultCreateInternalNewsFormValues}
                innerRef={formikRef}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {(formData) => {
                    const { isValid, isValidating, isSubmitting, dirty, values, errors } = formData;
                    const submitDisabled = isSubmitting || isValidating || !isValid || !dirty;

                    const publishFromMaxDate = !!values[ENewsFormCreationFields.PUBLISH_TO]
                        ? moment(values[ENewsFormCreationFields.PUBLISH_TO]).subtract(1, 'day')
                        : moment(TODAY_FULL_DATE).add(100, 'year');

                    const publishFromMinDate = moment(TODAY_FULL_DATE);

                    const publishToMaxDate = moment(TODAY_FULL_DATE).add(100, 'year');

                    const publishToMinDate = !!values[ENewsFormCreationFields.PUBLISH_FROM]
                        ? moment(values[ENewsFormCreationFields.PUBLISH_FROM]).add(1, 'day')
                        : moment(TODAY_FULL_DATE).add(1, 'day');

                    const handlePreviewClick = async () => {
                        const { USER_FILE, ...data } = values;
                        // Если приложен файл то нам нужно положить его в locationState в формате base64
                        if (USER_FILE) {
                            const fileBase64 = await fileToBase64(USER_FILE);
                            const previewLocationState = {
                                ...data,
                                recipientType: fileTab,
                                fileBase64,
                            };
                            appHistoryPush(RoutePath.newsPreview.replace(ROUTE_NEWS_ID, '1'), previewLocationState);
                        } else {
                            appHistoryPush(RoutePath.newsPreview.replace(ROUTE_NEWS_ID, '1'), data);
                        }
                    };

                    const handleFormSubmit = (e: SyntheticEvent) => {
                        e.preventDefault();
                        handleSubmit(formData.values, formData);
                    };

                    return (
                        <Form onSubmit={handleFormSubmit}>
                            <FormFieldInput
                                required
                                dataName="newsCreateInternal-title"
                                label={tForm.fields.title.title}
                                labelHint={<Typography variant="body2">{tForm.fields.title.hint}</Typography>}
                                name={ENewsFormCreationFields.TITLE}
                            />
                            <FormFieldTextarea
                                required
                                dataName="newsCreateInternal-markdown"
                                label={tForm.fields.markdown.title}
                                labelHint={
                                    <LinkBlock isExternalUrl target="_blank" url={tForm.fields.markdown.guideLink}>
                                        {tForm.fields.markdown.hint}
                                    </LinkBlock>
                                }
                                name={ENewsFormCreationFields.MARKDOWN}
                                rows={10}
                            />
                            <FormImageLinks
                                buttonText={tForm.fields.images.btnAdd}
                                dataName="newsCreateInternal-images"
                                inputPlaceholder={tForm.fields.images.placeholder}
                                label={tForm.fields.images.title}
                                labelHint={<Typography variant="body2">{tForm.fields.images.hint}</Typography>}
                                maxItems={MAX_IMAGES}
                                name={ENewsFormCreationFields.IMAGES}
                            />
                            <NewsFormDateContainerStyled>
                                <FormFieldDate
                                    required
                                    dataName="newsCreateInternal-fromDate"
                                    headType="separate"
                                    label={tForm.fields.publishFrom}
                                    maxDate={publishFromMaxDate}
                                    minDate={publishFromMinDate}
                                    name={ENewsFormCreationFields.PUBLISH_FROM}
                                />
                                <FormFieldDate
                                    additionalButtons={additionalPublishToButton}
                                    dataName="newsCreateInternal-toDate"
                                    disabled={!values[ENewsFormCreationFields.PUBLISH_FROM]}
                                    headType="separate"
                                    label={tForm.fields.publishTo}
                                    maxDate={publishToMaxDate}
                                    minDate={publishToMinDate}
                                    name={ENewsFormCreationFields.PUBLISH_TO}
                                />
                            </NewsFormDateContainerStyled>
                            <NewsFormRepeatContainerStyled>
                                <FormFieldInput
                                    dataName="newsCreateInternal-repeat"
                                    inputtype="number"
                                    label={tForm.fields.repeat}
                                    min={0}
                                    name={ENewsFormCreationFields.REPEAT}
                                />
                            </NewsFormRepeatContainerStyled>
                            <NewsFormFileContainerStyled>
                                <FormFileInput
                                    attachText={tForm.fields.file.attach}
                                    dataName="newsCreateInternal-file"
                                    error={errors[ENewsFormCreationFields.USER_FILE]}
                                    label={tForm.fields.file.title}
                                    labelHint={<Typography variant="body2">{tForm.fields.file.hint}</Typography>}
                                    name={ENewsFormCreationFields.USER_FILE}
                                >
                                    <TabMenuStyled autoWidth value={fileTab} onChange={handleTabChange}>
                                        <TabItem label={tForm.fields.file.tab1} value={ERecipientTypes.INN} />
                                        <TabItem label={tForm.fields.file.tab2} value={ERecipientTypes.ACCOUNT} />
                                    </TabMenuStyled>
                                </FormFileInput>
                            </NewsFormFileContainerStyled>

                            <NewsFormControlsContainerStyled>
                                <RequiredHintStyled>
                                    <RequiredHintAsteriskStyled>*</RequiredHintAsteriskStyled>
                                    <Typography variant="h5">{tRequiredHint}</Typography>
                                </RequiredHintStyled>
                                <ButtonsWrapperStyled>
                                    <Button color="info" variant="text" onClick={dirty ? handleCancel : handleRedirect}>
                                        {tForm.controls.cancel}
                                    </Button>
                                    <ArrowTooltip disableHoverListener={!submitDisabled} title={tForm.notValid}>
                                        <ButtonsTooltipWrapperStyled>
                                            <Button
                                                disabled={submitDisabled}
                                                size="small"
                                                type="submit"
                                                typographyProps={{ variant: 'h5' }}
                                            >
                                                {tForm.controls.submit}
                                            </Button>
                                            <Button
                                                disabled={submitDisabled}
                                                size="small"
                                                typographyProps={{ variant: 'h5' }}
                                                onClick={handlePreviewClick}
                                            >
                                                {tForm.controls.preview}
                                            </Button>
                                        </ButtonsTooltipWrapperStyled>
                                    </ArrowTooltip>
                                </ButtonsWrapperStyled>
                            </NewsFormControlsContainerStyled>
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
};

export default memo(NewsInternalForm);
